import { useBlocker, useRouterState } from '@buzola/router'
import { useBindxContext, usePersist } from '@contember/bindx-react'
import { Button } from '@contember/bindx-ui'
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { ConfirmDialog, ConfirmDialogCancel } from './confirm-dialog'
import { describePersistFailure } from './persist-feedback'

/**
 * Blocks leaving a form with unpersisted edits.
 *
 * Dirtiness is read from the batch persister's ChangeRegistry **on demand**,
 * not from the reactive `isDirty` flag: a stale `false` there silently
 * discarded the user's edits, e.g. when leaving the course form via
 * "Přidat osobu".
 *
 * Every admin route uses the styled three-way dialog (save / discard / stay).
 * Full-page unloads still use the browser's native `beforeunload` prompt.
 */
export function UnsavedChangesGuard() {
	const guard = useUnsavedChangesGuard()
	const [prompted, setPrompted] = useState(false)

	useEffect(() => {
		if (!guard.hasUserChanges) return

		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			event.preventDefault()
			event.returnValue = ''
		}

		window.addEventListener('beforeunload', handleBeforeUnload)
		return () => window.removeEventListener('beforeunload', handleBeforeUnload)
	}, [guard.hasUserChanges])

	useEffect(() => {
		if (!guard.isBlocked) {
			setPrompted(false)
			return
		}
		if (prompted) return

		// Nothing dirty → let the navigation through without bothering the user.
		if (!guard.hasUnsavedChanges()) {
			guard.proceed()
			return
		}

		setPrompted(true)
	}, [guard, prompted])

	return <UnsavedChangesDialog guard={guard} open={guard.isBlocked && prompted} />
}

/**
 * Three ways out, three weights: saving is the recommended action and the only
 * filled button (navy), discarding is rose — outline and label both — and
 * staying is a plain ghost. Discarding used to be red, which — next to the salmon primary and the
 * salmon warning heading — read as three competing alarms.
 */
function UnsavedChangesDialog({ guard, open }: { guard: UnsavedChangesGuardState; open: boolean }) {
	return (
		<ConfirmDialog
			actions={
				<>
					<ConfirmDialogCancel>
						<Button className="btn-ghost" disabled={guard.isSaving} type="button" variant="ghost">Zůstat</Button>
					</ConfirmDialogCancel>
					<Button
						className="btn-ghost border-orange text-orange hover:border-orange hover:bg-rose-muted/50 hover:text-orange"
						disabled={guard.isSaving}
						onClick={guard.discardAndProceed}
						type="button"
						variant="ghost"
					>
						Zahodit změny
					</Button>
					<Button className="btn-navy" disabled={guard.isSaving} onClick={() => void guard.saveAndProceed()} type="button">
						{guard.isSaving ? 'Ukládám…' : 'Uložit a pokračovat'}
					</Button>
				</>
			}
			description={
				<>
					Na téhle stránce máš rozpracované změny, které se ještě neuložily.
					{guard.error ? <span className="mt-2 block text-danger">{guard.error}</span> : null}
				</>
			}
			onOpenChange={nextOpen => {
				if (!nextOpen) guard.stay()
			}}
			open={open}
			title="Neuložené změny"
		/>
	)
}

export interface UnsavedChangesGuardState {
	isBlocked: boolean
	isSaving: boolean
	error: string | null
	/** Reads the ChangeRegistry at call time — never a cached flag. */
	hasUnsavedChanges: () => boolean
	/** Same verdict as {@link hasUnsavedChanges}, but re-rendered on store changes. */
	hasUserChanges: boolean
	proceed: () => void
	stay: () => void
	discardAndProceed: () => void
	saveAndProceed: () => Promise<void>
}

/** Dirty fields and relations of the whole store, serialized for comparison. */
type DirtyState = Map<string, string>

const interactionEvents = ['pointerdown', 'keydown', 'beforeinput', 'drop'] as const

/**
 * Navigation-blocking primitive behind the guard. Exposed on its own so a
 * route-local guard can reuse the dirtiness, discard and save-then-continue
 * logic without copying it.
 */
export function useUnsavedChangesGuard(): UnsavedChangesGuardState {
	const { persistAll } = usePersist()
	const { store, batchPersister } = useBindxContext()
	// The committed location, query included — it only advances once the blockers
	// let the navigation through, so it still identifies the page being left.
	const { location } = useRouterState()
	const route = `${location.pathname}${location.search}`
	const { state, proceed, reset } = useBlocker(true)
	const [isSaving, setIsSaving] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const getDirtyEntities = useCallback(
		() => batchPersister?.getChangeRegistry().getDirtyEntities() ?? [],
		[batchPersister],
	)

	const readDirtyState = useCallback((): DirtyState => {
		const dirty: DirtyState = new Map()

		for (const entity of getDirtyEntities()) {
			const prefix = `${entity.entityType}:${entity.entityId}`
			const data = store.getEntitySnapshot(entity.entityType, entity.entityId)?.data as Record<string, unknown> | undefined

			if (entity.changeType !== 'update') dirty.set(`${prefix}#${entity.changeType}`, '')
			for (const field of entity.dirtyFields) dirty.set(`${prefix}.${field}`, JSON.stringify(data?.[field]) ?? 'undefined')
			for (const relation of entity.dirtyRelations) dirty.set(`${prefix}@${relation}`, '')
		}

		return dirty
	}, [getDirtyEntities, store])

	/**
	 * Dirty state as of the moment the user first touched the current page —
	 * everything in it counts as machine-made, not as an edit worth guarding.
	 *
	 * The bindx store is app-wide and its dirtiness is a plain diff against
	 * server data, so it reports two things the user never did: writes a form
	 * makes while mounting (the repeater renumbers `sortableBy` fields to be
	 * 0-based, derived fields recompute) and leftovers of those writes, which
	 * outlive the form — bindx only drops never-persisted drafts on unmount.
	 * Without this baseline both a spotless form and a plain list page block
	 * navigation.
	 *
	 * Missing until the first interaction with the current visit: with nothing
	 * touched there is nothing to lose, whatever the store says.
	 */
	const baselineRef = useRef<{ route: string; dirty: DirtyState } | null>(null)

	useEffect(() => {
		const captureBaseline = () => {
			if (baselineRef.current?.route === route) return
			baselineRef.current = { route, dirty: readDirtyState() }
		}

		// Capture phase, so the baseline is taken before the event that starts a
		// navigation (or edits a field) is handled anywhere. `beforeinput` and
		// `drop` cover input that arrives without a preceding key or pointer event
		// — virtual keyboards, dropped uploads.
		for (const event of interactionEvents) window.addEventListener(event, captureBaseline, true)
		return () => {
			for (const event of interactionEvents) window.removeEventListener(event, captureBaseline, true)
			// The baseline belongs to one visit, not to a URL: kept for the next one
			// it would credit the user with everything the store collected in
			// between. Open a record from a listing, come back, and the form's
			// mount-time writes would read as edits made on the listing — which has
			// no form and nothing to save.
			baselineRef.current = null
		}
	}, [route, readDirtyState])

	const hasUnsavedChanges = useCallback(() => {
		const baseline = baselineRef.current
		if (baseline?.route !== route) return false

		for (const [key, value] of readDirtyState()) {
			if (baseline.dirty.get(key) !== value) return true
		}
		return false
	}, [route, readDirtyState])

	const hasUserChanges = useSyncExternalStore(
		useCallback(onChange => store.subscribe(onChange), [store]),
		hasUnsavedChanges,
	)

	const discardUnsavedChanges = useCallback(() => {
		for (const { entityType, entityId, changeType } of getDirtyEntities()) {
			if (changeType === 'create') {
				store.resetAllRelations(entityType, entityId)
				store.removeEntity(entityType, entityId)
				continue
			}

			if (changeType === 'delete') {
				store.unscheduleForDeletion(entityType, entityId)
			}

			store.resetEntity(entityType, entityId)
			store.resetAllRelations(entityType, entityId)
		}

		store.notify()
	}, [store, getDirtyEntities])

	const stay = useCallback(() => {
		setError(null)
		reset()
	}, [reset])

	const discardAndProceed = useCallback(() => {
		setError(null)
		discardUnsavedChanges()
		proceed()
	}, [discardUnsavedChanges, proceed])

	const saveAndProceed = useCallback(async () => {
		setIsSaving(true)
		setError(null)
		try {
			const result = await persistAll()
			if (!result.success) {
				setError(describePersistFailure(result, store).message)
				return
			}
			proceed()
		} catch (cause) {
			setError(cause instanceof Error ? cause.message : String(cause))
		} finally {
			setIsSaving(false)
		}
	}, [persistAll, proceed, store])

	return useMemo(() => ({
		isBlocked: state === 'blocked',
		isSaving,
		error,
		hasUnsavedChanges,
		hasUserChanges,
		proceed,
		stay,
		discardAndProceed,
		saveAndProceed,
	}), [state, isSaving, error, hasUnsavedChanges, hasUserChanges, proceed, stay, discardAndProceed, saveAndProceed])
}
